import { createContext, RefObject, useContext, useEffect, useLayoutEffect, useState } from 'react';
import { ContainerHeightContext, useContainerHeight } from '../container-context/index';
import { ContainerContext } from '../popover/context';

function setBrowserFullscreenElement(elementRef: HTMLElement) {
	if (typeof elementRef?.requestFullscreen !== 'undefined') {
		elementRef.requestFullscreen();
	}
}

function exitBrowserFullscreenElement() {
	if (typeof document.exitFullscreen !== 'undefined') {
		document.exitFullscreen();
	}
}

function getBrowserFullscreenElementProp() {
	if (typeof document.fullscreenElement !== 'undefined') {
		return 'fullscreenElement';
	} else {
		throw new Error('fullscreenElement is not supported by this browser');
	}
}

type FullScreenContextValue = {
	fullScreen: boolean;
	setFullScreen: (fullScreen: boolean) => void;
};

export const FullScreenContext = createContext<FullScreenContextValue | undefined>(undefined);
export const FullScreenContextProvider = (props: { children: any; containerElementRef: RefObject<HTMLDivElement> }) => {
	const [fullScreen, setFullScreen] = useState<boolean>(false);
	const measureContainer = useContainerHeight();

	useEffect(() => {
		if (props.containerElementRef.current) {
			if (fullScreen === true) {
				setBrowserFullscreenElement(props.containerElementRef.current);
				// without this the full screen appears black
				props.containerElementRef.current.style.background = 'white';
			} else if (document.fullscreenElement) {
				exitBrowserFullscreenElement();
			}
		}
	}, [fullScreen]);

	useLayoutEffect(() => {
		document.onfullscreenchange = () => setFullScreen(document[getBrowserFullscreenElementProp()] !== null);

		return () => (document.onfullscreenchange = undefined);
	});

	return (
		<FullScreenContext.Provider value={{ fullScreen, setFullScreen }}>
			<ContainerHeightContext
				measure={() => {
					const h = fullScreen ? props.containerElementRef.current?.getBoundingClientRect().height : measureContainer();
					return h;
				}}
			>
				<ContainerContext.Provider value={fullScreen ? props.containerElementRef.current : document.body}>
					{props.children}
				</ContainerContext.Provider>
			</ContainerHeightContext>
		</FullScreenContext.Provider>
	);
};

export const useFullScreenContext = () => {
	return useContext(FullScreenContext);
};
